import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSystemStatus, updateSystemStatus } from '../services/systemStatusService';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

// Create context
export const SystemStatusContext = createContext();

export const SystemStatusProvider = ({ children }) => {
  const { user } = useAuth();
  const [systemStatus, setSystemStatus] = useState({
    isActive: true,
    maintenanceMessage: '',
    startTime: null,
    endTime: null,
    expectedCompletionTime: null,
    affectedPages: ['all'],
    affectedModules: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maintenanceBypass, setMaintenanceBypass] = useState(false);

  // Fetch system status
  const fetchSystemStatus = async () => {
    try {
      setLoading(true);
      
      // Check if we're reloading due to a system status change
      const systemStatusChange = sessionStorage.getItem('system_status_change');
      
      if (systemStatusChange === 'true') {
        // Get the status from sessionStorage
        const isActive = sessionStorage.getItem('system_status_active') === 'true';
        console.log('[SystemStatusContext] Reloaded due to system status change, isActive:', isActive);
        
        // Clear the flags
        sessionStorage.removeItem('system_status_change');
        sessionStorage.removeItem('system_status_active');
        
        // Show a toast notification to inform the user
        if (isActive) {
          toast.success('Hệ thống đã được kích hoạt trở lại.');
        } else {
          toast.warning('Hệ thống đang trong chế độ bảo trì.');
        }
      }
      
      // Always fetch the latest status from the server
      const data = await getSystemStatus();
      
      // Check if system was auto-activated (compare with previous state)
      if (!systemStatus.isActive && data.isActive && data.maintenanceMessage.includes('tự động kích hoạt')) {
        toast.success('Hệ thống đã tự động kích hoạt sau khi kết thúc thời gian bảo trì.');
      }
      
      setSystemStatus(data);
      setError(null);
    } catch (err) {
      const isTransient = (
        err?.code === 'ERR_NETWORK'
        || err?.code === 'ECONNABORTED'
        || [502, 503, 504].includes(err?.response?.status)
      );

      if (!isTransient) {
        console.error('Error fetching system status:', err);
      }

      setError('Không thể tải trạng thái hệ thống');
      // Default to system active if there's an error
      setSystemStatus({
        isActive: true,
        maintenanceMessage: '',
        startTime: null,
        endTime: null,
        expectedCompletionTime: null,
        affectedPages: ['all'],
        affectedModules: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to bypass maintenance mode (for SUPER_ADMIN only)
  const bypassMaintenance = () => {
    if (user && user.role === 'SUPER_ADMIN') {
      setMaintenanceBypass(true);
      toast.info('Đã bỏ qua chế độ bảo trì. Một số tính năng có thể không hoạt động đúng.', {
        autoClose: 5000
      });
      
      // Store bypass in sessionStorage to persist across page refreshes
      sessionStorage.setItem('maintenance_bypass', 'true');
    }
  };

  // Function to exit maintenance bypass mode
  const exitMaintenanceBypass = () => {
    setMaintenanceBypass(false);
    sessionStorage.removeItem('maintenance_bypass');
    toast.info('Đã tắt chế độ bỏ qua bảo trì.');
  };

  // Function to toggle system maintenance mode (for SUPER_ADMIN only)
  const toggleMaintenanceMode = async (maintenanceData) => {
    if (user && user.role === 'SUPER_ADMIN') {
      try {
        setLoading(true);
        const updatedStatus = await updateSystemStatus({
          ...maintenanceData,
          isActive: !systemStatus.isActive
        });
        
        setSystemStatus(updatedStatus);
        
        if (updatedStatus.isActive) {
          toast.success('Hệ thống đã được kích hoạt trở lại.');
        } else {
          toast.warning('Hệ thống đã chuyển sang chế độ bảo trì.');
        }
        
        return updatedStatus;
      } catch (err) {
        console.error('Error toggling maintenance mode:', err);
        toast.error('Không thể thay đổi trạng thái hệ thống.');
        throw err;
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('Bạn không có quyền thực hiện thao tác này.');
      throw new Error('Unauthorized');
    }
  };

  // Check for maintenance bypass in sessionStorage on mount
  useEffect(() => {
    const bypassStatus = sessionStorage.getItem('maintenance_bypass');
    if (bypassStatus === 'true' && user && user.role === 'SUPER_ADMIN') {
      setMaintenanceBypass(true);
    }
  }, [user]);

  // Fetch system status on mount and every 5 minutes
  useEffect(() => {
    fetchSystemStatus();
    
    // Set up interval to check system status every 5 minutes
    const intervalId = setInterval(fetchSystemStatus, 5 * 60 * 1000);
    
    // Lắng nghe sự kiện thay đổi trạng thái hệ thống từ WebSocket
    const handleSystemStatusChange = (event) => {
      console.log('[SystemStatusContext] Received system_status_change event:', event.detail);
      
      try {
        // Validate the event data
        if (!event.detail) {
          console.error('[SystemStatusContext] Invalid event data:', event);
          return;
        }
        
        // Cập nhật trạng thái hệ thống từ sự kiện
        setSystemStatus(prevStatus => ({
          ...prevStatus,
          ...event.detail
        }));
        
        // Hiển thị thông báo cho người dùng
        if (event.detail.isActive) {
          // Check if this is an auto-activation
          if (event.detail.autoActivated) {
            // Hệ thống đã tự động kích hoạt sau khi kết thúc thời gian bảo trì
            toast.success('Hệ thống đã tự động kích hoạt sau khi kết thúc thời gian bảo trì. Trang sẽ tự động tải lại sau 3 giây.');
          } else {
            // Hệ thống đã được kích hoạt trở lại thủ công
            toast.success('Hệ thống đã được kích hoạt trở lại. Trang sẽ tự động tải lại sau 3 giây.');
          }
        } else {
          // Hệ thống đang chuyển sang chế độ bảo trì
          toast.warning('Hệ thống đang chuyển sang chế độ bảo trì. Trang sẽ tự động tải lại sau 3 giây.');
        }
        
        // Set a flag in sessionStorage to indicate that we're reloading due to system status change
        sessionStorage.setItem('system_status_change', 'true');
        sessionStorage.setItem('system_status_active', event.detail.isActive ? 'true' : 'false');
        
        // Tự động reload trang sau 3 giây để áp dụng trạng thái mới
        setTimeout(() => {
          console.log('[SystemStatusContext] Auto-reloading page due to system status change');
          window.location.reload();
        }, 3000);
      } catch (error) {
        console.error('[SystemStatusContext] Error handling system status change:', error);
        
        // Even if there's an error, try to reload the page
        setTimeout(() => {
          console.log('[SystemStatusContext] Forcing page reload after error');
          window.location.reload();
        }, 3000);
      }
    };
    
    // Đăng ký lắng nghe sự kiện
    window.addEventListener('system_status_change', handleSystemStatusChange);
    
    // Clean up interval and event listener on unmount
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('system_status_change', handleSystemStatusChange);
    };
  }, []);

  // Check if current page is affected by maintenance
  const isPageAffected = (path) => {
    // If system is active, no pages are affected
    if (systemStatus.isActive) return false;
    
    // If maintenance is bypassed, no pages are affected
    if (maintenanceBypass) return false;
    
    // If SUPER_ADMIN, no pages are affected (they can always access)
    if (user && user.role === 'SUPER_ADMIN') return false;
    
    // If affectedPages includes 'all', all pages are affected
    if (systemStatus.affectedPages && systemStatus.affectedPages.includes('all')) return true;
    
    // Check if current path is in affected pages
    return systemStatus.affectedPages && systemStatus.affectedPages.includes(path);
  };

  // Format maintenance time range for display
  const getMaintenanceTimeRange = () => {
    if (!systemStatus.startTime && !systemStatus.endTime) return '';
    
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('vi-VN');
    };
    
    const startTime = formatDate(systemStatus.startTime);
    const endTime = formatDate(systemStatus.endTime);
    
    if (startTime && endTime) {
      return `từ ${startTime} đến ${endTime}`;
    } else if (startTime) {
      return `bắt đầu từ ${startTime}`;
    } else if (endTime) {
      return `đến ${endTime}`;
    }
    
    return '';
  };

  return (
    <SystemStatusContext.Provider 
      value={{ 
        systemStatus, 
        loading, 
        error, 
        isLoading: loading,
        isPageAffected, 
        getMaintenanceTimeRange,
        refreshSystemStatus: fetchSystemStatus,
        bypassMaintenance,
        exitMaintenanceBypass,
        maintenanceBypass,
        toggleMaintenanceMode
      }}
    >
      {children}
    </SystemStatusContext.Provider>
  );
};

export const useSystemStatus = () => {
  const context = useContext(SystemStatusContext);
  if (!context) {
    throw new Error('useSystemStatus must be used within a SystemStatusProvider');
  }
  return context;
};